// Home page: auth gate, device dialog, screen/camera/mic toggles, live
// camera preview, small record control, HUD stats, library tiles,
// crash-recovery banner.

import { Journal } from './journal';
import { PartUploader, DEFAULT_PART_SIZE, type UploaderStats } from './uploader';
import {
  acquireDisplayStream,
  acquireUserStream,
  prepareRecording,
  detectContainer,
  detectMimeType,
  type RecorderSession,
  type CaptureMode,
} from './recorder';
import { fmtClock } from './orbit';
import { RecordControl, countdown } from './record-control';
import { showToast } from './toast';
import { fetchCurrentUser, logout, type CurrentUser } from './auth';
import {
  loadPreferences,
  savePreferences,
  listDevices,
  type CapturePreferences,
  type DisplayModePreference,
} from './device-preferences';
import { registerServiceWorker } from './sw-register';

registerServiceWorker();

const MAX_DURATION_S = 3600; // 1 hour hard cap
const COUNTDOWN_S = 3;
/** SC-17 follow-up: how often the live session pings PUT
 * .../:slug/heartbeat while active (see startHeartbeat() below). A codex
 * review found the original design bumped last_heartbeat_at ONLY on a full
 * part-upload, whose cadence tracks encoder throughput, not wall-clock
 * aliveness -- a low-motion screen share can take well over the old 3-minute
 * staleness threshold to accumulate a single 10 MiB part, so a genuinely
 * live session could get swept into 'failed' before ever uploading one.
 * This timer is now the reliable floor signal, entirely decoupled from
 * upload cadence; the part-upload heartbeat bump server-side stays too, as
 * a free extra signal.
 *
 * This interval is a *foreground* best case, not a guarantee: a hidden
 * tab's setInterval() can be throttled by the browser (see the
 * document.addEventListener('visibilitychange', ...) catch-up ping below,
 * and src/worker/index.ts's HEARTBEAT_STALE_MS for the full reasoning
 * about background-tab throttling and why the server's grace margin is
 * sized well past a simple multiple of this number). */
const HEARTBEAT_PING_MS = 25_000;
const DISPLAY_SURFACE: Record<DisplayModePreference, 'monitor' | 'window' | 'browser'> = {
  screen: 'monitor',
  window: 'window',
  browser: 'browser',
};
const supportsDisplayMedia = typeof navigator.mediaDevices?.getDisplayMedia === 'function';

const $ = <T = HTMLElement>(id: string): T => document.getElementById(id) as unknown as T;

const params = new URLSearchParams(location.search);
const groupSlug = location.pathname.startsWith('/g/') ? decodeURIComponent(location.pathname.slice(3)) : null;
// Test hooks (documented in README): ?part=<bytes> ?vbps=<bps> ?noise=1 ?state=<record state>
const partSize = params.has('part') ? parseInt(params.get('part')!, 10) : DEFAULT_PART_SIZE;
const vbps = params.has('vbps') ? parseInt(params.get('vbps')!, 10) : undefined;
const noiseOverlay = params.get('noise') === '1';
const debugState = params.get('state');

const journal = new Journal();
const control = new RecordControl($('preview-box'));
const announce = $('announcements');
const originalDocumentTitle = document.title;
const favicon = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
const originalFaviconHref = favicon?.getAttribute('href') ?? '';
const recordingFaviconHref = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='12' r='10' fill='%23e54838'/%3E%3C/svg%3E";

function setRecordingTab(isRecording: boolean, clock?: string, paused = false): void {
  document.title = isRecording ? `${clock} · ${paused ? 'Paused' : 'Recording'} — clip` : originalDocumentTitle;
  if (favicon) favicon.setAttribute('href', isRecording ? recordingFaviconHref : originalFaviconHref);
}

function setPauseResumeControl(visible: boolean, paused = false): void {
  const button = $<HTMLButtonElement>('pause-resume-btn');
  button.classList.toggle('hidden', !visible);
  button.setAttribute('aria-label', paused ? 'Resume recording' : 'Pause recording');
  button.querySelector('.pause-icon')?.classList.toggle('hidden', paused);
  button.querySelector('.resume-icon')?.classList.toggle('hidden', !paused);
  $('recording-indicator-text').textContent = paused ? 'Paused' : 'Recording';
}

interface ActiveRecording {
  slug: string;
  watchUrl: string;
  session: RecorderSession;
  uploader: PartUploader;
  timerHandle: number;
  capHandle: number;
  /** See startHeartbeat() -- pings PUT .../:slug/heartbeat on HEARTBEAT_PING_MS. */
  heartbeatHandle: number;
}

let active: ActiveRecording | null = null;
let currentGroup: { name: string; slug: string; role: 'owner' | 'member' } | null = null;
let prefs: CapturePreferences = loadPreferences();
let fallbackPending = false;
let cancelCountdown = false;
let allowAllCaptureOff = false;
/** Camera+mic stream shown live in the preview box before Record is pressed.
 * Released and re-acquired fresh when recording actually starts, so the
 * recorder owns its own stream lifecycle. */
let livePreviewStream: MediaStream | null = null;
let screenPreviewStream: MediaStream | null = null;
let livePreviewSecondCamStream: MediaStream | null = null;
/** True from the moment startCameraPreview() commits to acquiring a camera
 * stream until that getUserMedia() round trip actually settles (SC-40). On
 * real phones getDisplayMedia is unsupported, so initToggleRow() forces the
 * camera on and calls startCameraPreview() automatically at page load — that
 * network/hardware round trip genuinely takes a couple hundred ms, and
 * updateStudioMode() previously only flipped into the full-screen mobile
 * layout once it resolved. That let the browser paint the normal, scrollable
 * desktop-style layout first and visibly snap into the full-screen layout
 * afterward. This flag lets updateStudioMode() commit to the studio layout
 * synchronously, before the stream lands, so there is nothing left to flash. */
let cameraPreviewPending = false;
/** True for the entire span of beginFlow(), from the moment it releases the
 * live preview streams (to hand them to the new recording) until the
 * recording either actually starts (is-recording lands) or the attempt is
 * aborted/fails and control reverts to idle. beginFlow() releases the
 * preview via stopCameraPreview()/takeScreenPreview(), and BOTH of those
 * call updateStudioMode() synchronously the instant they null out their
 * stream var -- with no canvas yet appended and is-recording not yet set,
 * hasPreview reads false for that whole window (camera/mic permission +
 * the /api/recordings round trip + prepareRecording()), snapping is-studio
 * off and back on and flashing the non-studio mobile layout (topbar,
 * toggle row, details block, etc.) in between. This flag keeps
 * updateStudioMode() committed to the studio layout across that handoff,
 * the same way cameraPreviewPending does for the page-load auto-start. */
let recordingStarting = false;
let hasMultipleCameras = false;
let flippingCamera = false;
let dualCameraEnabled = false;
let removeOrientationListener: (() => void) | null = null;
// y: 0.86 was the original default but genuinely overlaps the always-visible
// .preview-controls bar at the bottom of the preview box -- confirmed across
// small/desktop/iPhone-width viewports, the bubble's bottom edge extended
// PAST the box edge and sat on top of the "Select screen" button, silently
// intercepting drag attempts meant for the bubble. 0.35 keeps the whole
// bubble comfortably above the controls bar (which starts as high as ~0.57
// of the box height on the narrowest tested viewport) in every case tested.
let bubblePosition = { x: 0.86, y: 0.35 };
let bubbleScale = 1;
let bubbleSession: RecorderSession | null = null;
// SC-42: cursor:grab (the bubble's only prior drag affordance) never reads
// on touch -- there's no hover state to reveal it -- so a first-time mobile
// user had zero visual sign the bubble was interactive at all. Gated on
// localStorage (once ever, not once per tab session) rather than nagging on
// every recording; bubbleHintFlashed just stops this same page load from
// re-triggering it on every applyPreviewBubble() call while the bubble is
// visible (drag/pinch/resize all funnel through that function).
const BUBBLE_HINT_SEEN_KEY = 'clip.bubble-drag-hint-seen.v1';
let bubbleHintFlashed = false;
// SC-22: cached, lazily-built swap-cameras icon -- inserted into the
// existing drag-hint pill (only) while a second physical camera is live, so
// tapping the bubble reads as "swap cameras" rather than only "drag me".
let bubbleSwapIconEl: SVGSVGElement | null = null;
// Codex-found race: the bubble tap handler fires swapDualCameras() unawaited,
// and a swap's srcObject assignment + await play() gap only updates
// stream-tracking state (currentCamStream/secondCamStream in recorder.ts, or
// livePreviewStream/livePreviewSecondCamStream here) AFTER that await. Two
// taps landing within that gap can interleave their reads/writes of that
// state, leaving the visibly-displayed primary out of sync with what's
// tracked as current -- breaking unplug/mute detection for one camera. A
// tap while a swap is already in flight is simply ignored rather than
// queued: swaps are near-instant, so this doesn't read as unresponsive, and
// it avoids reasoning about a queue of swaps each racing the next.
let dualCameraSwapInFlight = false;

interface BubbleGeometry {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface BubblePointer {
  x: number;
  y: number;
}

const bubblePointers = new Map<number, BubblePointer>();
// `moved` distinguishes a tap (swap cameras) from a drag (reposition the
// bubble) -- see onBubblePointerMove()/bindBubbleInteractions()'s pointerup
// handler. It only ever tracks total excursion from the pointerdown origin
// (bubbleDrag.x/y, never updated after down), not the final displacement, so
// a drag that happens to end back near its start still counts as a drag.
let bubbleDrag: { target: HTMLElement; pointerId: number; x: number; y: number; position: { x: number; y: number }; moved: boolean } | null = null;
let bubblePinch: { distance: number; scale: number } | null = null;
// Pointer hardware can report faster than the browser can paint. Keep the
// pointer bookkeeping synchronous, but coalesce geometry reads and visual
// writes to the next animation frame: only that final position is visible.
let bubbleMoveFrame: number | null = null;
// Squared-distance tap/drag threshold (8px), compared against squared
// deltas to avoid a sqrt on every pointermove.
const BUBBLE_TAP_MOVE_THRESHOLD_SQ = 8 * 8;

function clampBubbleScale(scale: number): number {
  return Math.min(2.5, Math.max(0.5, scale));
}

function containedGeometry(element: HTMLCanvasElement): BubbleGeometry {
  const rect = element.getBoundingClientRect();
  const intrinsicRatio = element.width / element.height;
  const boxRatio = rect.width / rect.height;
  if (boxRatio > intrinsicRatio) {
    const height = rect.height;
    const width = height * intrinsicRatio;
    return { left: rect.left + (rect.width - width) / 2, top: rect.top, width, height };
  }
  const width = rect.width;
  const height = width / intrinsicRatio;
  return { left: rect.left, top: rect.top + (rect.height - height) / 2, width, height };
}

function previewGeometry(): BubbleGeometry {
  const box = $('preview-box');
  const rect = box.getBoundingClientRect();
  return { left: rect.left + box.clientLeft, top: rect.top + box.clientTop, width: box.clientWidth, height: box.clientHeight };
}

function bubbleRadius(geometry: BubbleGeometry): number {
  return Math.min(geometry.width * 0.3, 150) * bubbleScale / 2;
}

function clampBubblePosition(position: { x: number; y: number }, geometry?: BubbleGeometry): { x: number; y: number } {
  if (bubbleSession) {
    const radius = 140 * bubbleScale;
    const minX = Math.min(0.5, radius / 2 / bubbleSession.canvas.width);
    const minY = Math.min(0.5, radius / 2 / bubbleSession.canvas.height);
    return { x: Math.min(1 - minX, Math.max(minX, position.x)), y: Math.min(1 - minY, Math.max(minY, position.y)) };
  }
  if (!geometry) return { x: Math.min(1, Math.max(0, position.x)), y: Math.min(1, Math.max(0, position.y)) };
  const radius = bubbleRadius(geometry);
  const minX = Math.min(0.5, radius / 2 / geometry.width);
  const minY = Math.min(0.5, radius / 2 / geometry.height);
  return { x: Math.min(1 - minX, Math.max(minX, position.x)), y: Math.min(1 - minY, Math.max(minY, position.y)) };
}

function activePreviewBubble(): HTMLVideoElement | null {
  const box = $('preview-box');
  if (box.dataset.previewBubble !== 'true') return null;
  const id = box.dataset.mode === 'screen' ? 'camera-preview-video' : 'camera-preview-video-2';
  const video = $<HTMLVideoElement>(id);
  return video.classList.contains('hidden') ? null : video;
}

/** SC-42: shows the always-on grab-dots affordance once per page load the
 * first time a bubble actually appears, and -- the first time EVER for this
 * browser (localStorage-gated) -- pulses it briefly so a first-time user's
 * eye actually catches it instead of it blending into the video underneath. */
function maybeFlashBubbleHint(hint: HTMLElement): void {
  if (bubbleHintFlashed) return;
  bubbleHintFlashed = true;
  let seen = true;
  try {
    seen = localStorage.getItem(BUBBLE_HINT_SEEN_KEY) === '1';
    if (!seen) localStorage.setItem(BUBBLE_HINT_SEEN_KEY, '1');
  } catch {
    return; // storage blocked/unavailable -- skip the one-time pulse rather than risk showing it every load
  }
  if (seen) return;
  hint.classList.add('is-first-time');
  window.setTimeout(() => hint.classList.remove('is-first-time'), 2600);
}

/** SC-22: lazily builds (once) a small two-arrow swap glyph matching the
 * app's existing stroke-only icon style (compare flip-camera-btn's SVG in
 * record.html), sized to sit inline with the drag-hint pill's existing dots.
 * Built via a detached wrapper rather than the DOM SVG API purely for
 * brevity -- innerHTML parses inline SVG markup the same either way. */
function ensureBubbleSwapIcon(): SVGSVGElement {
  if (bubbleSwapIconEl) return bubbleSwapIconEl;
  const wrapper = document.createElement('div');
  wrapper.innerHTML =
    '<svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" style="flex:none">' +
    '<path d="M4 8h13M13 4l4 4-4 4" fill="none" stroke="var(--cream)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M20 16H7M11 12l-4 4 4 4" fill="none" stroke="var(--cream)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';
  bubbleSwapIconEl = wrapper.firstElementChild as SVGSVGElement;
  return bubbleSwapIconEl;
}

function applyPreviewBubble(): void {
  const video = activePreviewBubble();
  const hint = $('bubble-grab-hint');
  for (const id of ['camera-preview-video', 'camera-preview-video-2']) {
    const candidate = $<HTMLVideoElement>(id);
    if (candidate !== video) {
      candidate.removeAttribute('style');
      candidate.removeAttribute('aria-label');
      candidate.title = '';
    }
  }
  if (video) {
    const geometry = previewGeometry();
    bubblePosition = clampBubblePosition(bubblePosition, geometry);
    const size = bubbleRadius(geometry) * 2;
    const left = `${bubblePosition.x * geometry.width}px`;
    const top = `${bubblePosition.y * geometry.height}px`;
    video.style.left = left;
    video.style.top = top;
    video.style.width = `${size}px`;
    video.style.height = `${size}px`;
    video.style.transform = 'translate(-50%, -50%)';
    // Mirrors the video's own box exactly (see above) so the dot-pill --
    // anchored to its bottom edge via CSS padding -- tracks the bubble
    // through every drag/resize without its own geometry math.
    hint.style.left = left;
    hint.style.top = top;
    hint.style.width = `${size}px`;
    hint.style.height = `${size}px`;
    hint.style.transform = 'translate(-50%, -50%)';
    hint.classList.remove('hidden');
    maybeFlashBubbleHint(hint);
    // Swap-cameras affordance only makes sense with two live physical
    // cameras (dual mode) -- 'both' mode's bubble is the only camera feed
    // there is, nothing to swap it with.
    const pill = hint.querySelector('.bubble-grab-hint-pill');
    if (dualCameraEnabled) {
      video.setAttribute('aria-label', 'Tap to swap cameras');
      video.title = 'Tap to swap cameras';
      const icon = ensureBubbleSwapIcon();
      // insertBefore (a Node method), not the DOM-standard Element.prepend --
      // @cloudflare/workers-types declares its own global `Element` (for
      // HTMLRewriter) that merges with lib.dom's, and its prepend()/append()
      // overloads take HTMLRewriter content types (string|ReadableStream|
      // Response), not Node -- see tsconfig.json's global workers-types
      // include.
      if (pill && !pill.contains(icon)) pill.insertBefore(icon, pill.firstChild);
    } else {
      video.removeAttribute('aria-label');
      video.title = '';
      bubbleSwapIconEl?.remove();
    }
  } else {
    hint.classList.add('hidden');
    bubbleSwapIconEl?.remove();
  }
  // E2E hook, mirrors the existing __clipDebug pattern elsewhere in this
  // file: exposes live, post-clamp bubble state (not just a
  // once-per-recording snapshot, since dragging/zooming happens
  // continuously before and during a recording, and the bubble's INITIAL
  // position is set here directly rather than always routing through
  // syncBubble()) so tests can assert on the real internal position
  // instead of only what's visually rendered.
  (window as unknown as { __clipBubbleDebug?: unknown }).__clipBubbleDebug = { position: bubblePosition, scale: bubbleScale };
}

function syncBubble(position = bubblePosition, scale = bubbleScale, geometry?: BubbleGeometry): void {
  bubbleScale = clampBubbleScale(scale);
  bubblePosition = clampBubblePosition(position, geometry);
  bubbleSession?.setBubblePosition(bubblePosition.x, bubblePosition.y);
  bubbleSession?.setBubbleScale(bubbleScale);
  applyPreviewBubble();
}

function bubbleCircle(geometry: BubbleGeometry, canvas = false): { x: number; y: number; radius: number } {
  const radius = canvas ? 140 * bubbleScale * geometry.width / (bubbleSession?.canvas.width ?? geometry.width) : bubbleRadius(geometry);
  return { x: geometry.left + bubblePosition.x * geometry.width, y: geometry.top + bubblePosition.y * geometry.height, radius };
}

function pointerIsOnBubble(target: HTMLElement, event: Pick<MouseEvent, 'clientX' | 'clientY'>): boolean {
  // A bubble only actually exists in 'both'/'dual' mode -- without this
  // check, a pointerdown in the phantom bubble-sized corner of a FULL-FRAME
  // (non-bubble) preview would still register as a drag, silently drifting
  // bubblePosition for whenever bubble mode is later entered, even though
  // nothing is visually there to grab. getCaptureMode() is read live (not
  // a cached class) specifically so this stays correct if dual camera gets
  // activated mid-recording, well after the canvas was first shown.
  if (target instanceof HTMLCanvasElement) {
    const mode = getCaptureMode();
    if (mode !== 'both' && mode !== 'dual') return false;
  } else if (activePreviewBubble() !== target) {
    return false;
  }
  const geometry = target instanceof HTMLCanvasElement ? containedGeometry(target) : previewGeometry();
  const circle = bubbleCircle(geometry, target instanceof HTMLCanvasElement);
  return Math.hypot(event.clientX - circle.x, event.clientY - circle.y) <= circle.radius;
}

function pointerDistance(): number | null {
  const pointers = [...bubblePointers.values()];
  return pointers.length >= 2 ? Math.hypot(pointers[0].x - pointers[1].x, pointers[0].y - pointers[1].y) : null;
}

function finishBubblePointer(target: HTMLElement, pointerId: number): void {
  // Do not lose the final position when pointerup arrives before the queued
  // animation frame. This intentionally runs before clearing pointer state.
  if (bubbleMoveFrame !== null) {
    window.cancelAnimationFrame(bubbleMoveFrame);
    bubbleMoveFrame = null;
    applyPendingBubbleMove();
  }
  bubblePointers.delete(pointerId);
  if (target.hasPointerCapture(pointerId)) target.releasePointerCapture(pointerId);
  if (bubbleDrag?.pointerId === pointerId) bubbleDrag = null;
  if (bubblePointers.size < 2) bubblePinch = null;
  if (bubblePointers.size === 0) target.classList.remove('is-bubble-dragging');
}

function onBubblePointerDown(event: PointerEvent): void {
  const target = event.currentTarget as HTMLElement;
  if (!pointerIsOnBubble(target, event)) return;
  event.preventDefault();
  target.setPointerCapture(event.pointerId);
  target.classList.add('is-bubble-dragging');
  bubblePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
  const distance = pointerDistance();
  if (distance !== null) {
    bubblePinch = { distance, scale: bubbleScale };
    bubbleDrag = null;
    return;
  }
  bubbleDrag = { target, pointerId: event.pointerId, x: event.clientX, y: event.clientY, position: { ...bubblePosition }, moved: false };
}

function onBubblePointerMove(event: PointerEvent): void {
  if (!bubblePointers.has(event.pointerId)) return;
  bubblePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
  if (!bubblePinch && (!bubbleDrag || bubbleDrag.pointerId !== event.pointerId)) return;
  if (bubbleDrag && !bubbleDrag.moved) {
    const dx = event.clientX - bubbleDrag.x;
    const dy = event.clientY - bubbleDrag.y;
    if (dx * dx + dy * dy > BUBBLE_TAP_MOVE_THRESHOLD_SQ) bubbleDrag.moved = true;
  }
  if (bubbleMoveFrame === null) {
    bubbleMoveFrame = window.requestAnimationFrame(() => {
      bubbleMoveFrame = null;
      applyPendingBubbleMove();
    });
  }
}

function applyPendingBubbleMove(): void {
  if (bubblePinch) {
    const distance = pointerDistance();
    if (distance) syncBubble(bubblePosition, bubblePinch.scale * distance / bubblePinch.distance);
    return;
  }
  if (!bubbleDrag) return;
  const point = bubblePointers.get(bubbleDrag.pointerId);
  if (!point) return;
  const geometry = bubbleDrag.target instanceof HTMLCanvasElement ? containedGeometry(bubbleDrag.target) : previewGeometry();
  syncBubble({
    x: bubbleDrag.position.x + (point.x - bubbleDrag.x) / geometry.width,
    y: bubbleDrag.position.y + (point.y - bubbleDrag.y) / geometry.height,
  }, bubbleScale, geometry);
}

function onBubbleWheel(event: WheelEvent): void {
  const target = event.currentTarget as HTMLElement;
  if (!pointerIsOnBubble(target, event)) return;
  event.preventDefault();
  syncBubble(bubblePosition, bubbleScale * Math.exp(-event.deltaY * 0.0015));
}

/** SC-22: swaps which physical camera renders full-frame vs. bubble, tapped
 * (not dragged) on the bubble. Reuses the two already-live tracks via
 * recorder.ts's swapDualCameraStreams()/a direct srcObject exchange -- never
 * re-acquires getUserMedia, which would be slow and could retrigger the
 * hardware-contention failure SC-35's sequential dual-camera acquisition
 * fixed. Deliberately camera-only: 'both' mode's bubble is a screen-share's
 * lone camera feed, nothing physical to swap it with.
 *
 * Guarded by dualCameraSwapInFlight against overlapping taps -- see that
 * flag's comment. The guard has to live here, wrapping the whole function,
 * because this one entry point covers BOTH racy paths: the active-recording
 * handoff to recorder.ts's swapDualCameraStreams() and the pre-recording
 * raw srcObject swap below, and a tap can land while either is mid-flight. */
async function swapDualCameras(): Promise<void> {
  if (!dualCameraEnabled || dualCameraSwapInFlight) return;
  dualCameraSwapInFlight = true;
  try {
    if (active) {
      try {
        await active.session.swapDualCameraStreams();
      } catch (err) {
        console.warn('[clip] camera swap failed', err);
      }
      return;
    }
    if (!livePreviewStream || !livePreviewSecondCamStream) return;
    const primaryVideo = $<HTMLVideoElement>('camera-preview-video');
    const secondaryVideo = $<HTMLVideoElement>('camera-preview-video-2');
    const oldPrimary = livePreviewStream;
    const oldSecondary = livePreviewSecondCamStream;
    primaryVideo.srcObject = oldSecondary;
    secondaryVideo.srcObject = oldPrimary;
    try {
      await Promise.all([primaryVideo.play(), secondaryVideo.play()]);
    } catch (err) {
      primaryVideo.srcObject = oldPrimary;
      secondaryVideo.srcObject = oldSecondary;
      console.warn('[clip] camera swap failed', err);
      return;
    }
    livePreviewStream = oldSecondary;
    livePreviewSecondCamStream = oldPrimary;
    applyCameraPreviewAspect(livePreviewStream);
    applyPreviewBubble();
  } finally {
    dualCameraSwapInFlight = false;
  }
}

function bindBubbleInteractions(element: HTMLElement): void {
  element.addEventListener('pointerdown', onBubblePointerDown);
  element.addEventListener('pointermove', onBubblePointerMove);
  element.addEventListener('pointerup', (event) => {
    // A tap (as opposed to a drag) swaps cameras instead of repositioning
    // the bubble -- captured here, before finishBubblePointer() clears
    // bubbleDrag, since that's the only place both "this pointer was the
    // single active drag" and "it never moved past the tap threshold" are
    // still known together.
    const isTap = !!bubbleDrag && bubbleDrag.pointerId === event.pointerId && !bubbleDrag.moved && bubblePointers.size === 1;
    finishBubblePointer(element, event.pointerId);
    if (isTap) void swapDualCameras();
  });
  element.addEventListener('pointercancel', (event) => finishBubblePointer(element, event.pointerId));
  element.addEventListener('wheel', onBubbleWheel, { passive: false });
}

function fmtBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MiB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KiB`;
  return `${n} B`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Library thumbnail width in CSS px (SC-115) -- a card in .library-grid
 * never renders wider than ~280px (minmax(220px, 1fr) at typical viewport
 * widths), so this comfortably covers it at 1x-2x density without shipping
 * full recording resolution (the composite canvas is up to 1920x1080) into
 * a small D1 TEXT column and every library-list JSON response. */
const THUMBNAIL_WIDTH = 320;
/** Low JPEG quality is deliberate and fine here -- this is a small preview
 * tile, not the actual playable recording (that stays untouched in R2 at
 * full quality). Keeps the base64 data URI small: measured in practice at
 * a few KB to ~15KB for a real captured frame at this width/quality. */
const THUMBNAIL_JPEG_QUALITY = 0.6;

/** Grabs one representative static frame from the recording's own compositing
 * canvas (client/recorder.ts's `canvas` -- the same one driving
 * canvas.captureStream(FPS), continuously repainted by `paintTimer` for the
 * entire session) and downsamples it into a small JPEG data URI for the
 * library grid's card preview.
 *
 * Capture point: called from stopRecording() right before rec.session.stop()
 * -- i.e. at the LAST possible instant paintTimer is still running, not at
 * recording start. Grabbing frame 0 would risk a blank/black tile: the
 * composite canvas fills with a dark background color and only draws real
 * video once a source <video> element actually has decoded a frame, which
 * can lag the very start of prepareRecording() by one or more paint ticks.
 * Waiting until just before stop() sidesteps that entirely (by then the
 * canvas has been painting real content, uninterrupted, for the whole
 * recording) and also means the thumbnail reflects near-final content
 * rather than the first instant -- generally the more representative frame
 * for a "what is this recording" preview. This never grabs a frame from a
 * <video> element streamed over the network -- it is a synchronous, local
 * canvas readback, so rendering N of these in a library grid costs zero
 * network requests.
 *
 * Returns null (never throws) on any failure -- a missing thumbnail just
 * falls back to the library card's existing placeholder tile, same as
 * before this feature existed. */
function captureThumbnailDataUri(canvas: HTMLCanvasElement): string | null {
  try {
    if (!canvas.width || !canvas.height) return null;
    const scale = Math.min(1, THUMBNAIL_WIDTH / canvas.width);
    const width = Math.max(1, Math.round(canvas.width * scale));
    const height = Math.max(1, Math.round(canvas.height * scale));
    const thumbCanvas = document.createElement('canvas');
    thumbCanvas.width = width;
    thumbCanvas.height = height;
    const ctx = thumbCanvas.getContext('2d', { alpha: false });
    if (!ctx) return null;
    ctx.drawImage(canvas, 0, 0, width, height);
    return thumbCanvas.toDataURL('image/jpeg', THUMBNAIL_JPEG_QUALITY);
  } catch (err) {
    console.warn('[clip] thumbnail capture failed', err);
    return null;
  }
}

function setDeviceStatus(text: string, tone: 'ok' | 'warn' | 'error' = 'ok'): void {
  const el = $('device-status');
  el.textContent = text;
  el.dataset.tone = tone;
}

let dualCameraNoticeEl: HTMLDivElement | null = null;
let dualCameraNoticeHideTimer = 0;

/**
 * SC-28: attemptDualCamera()'s persistent #device-status message is meant to
 * stay visible "so a user who glances back a few seconds later still sees
 * why" -- but is-studio's mobile CSS hides #device-status entirely as soon
 * as any camera preview is live, which (in the realistic flow) is already
 * true by the time a user can even tap the dual-camera button to trigger
 * this fallback. Rather than un-hiding #device-status generally (SC-35
 * deliberately declutters is-studio), this renders a small, separate,
 * dedicated notice anchored right next to the dual-camera button itself,
 * inside .preview-controls -- which is NOT in is-studio's hidden list, so
 * it stays visible there too. Styled to match .toast/.device-status (same
 * ink/cream palette, warn-tone accent) rather than inventing new visual
 * language.
 */
function showDualCameraNotice(text: string, ms = 4500): void {
  const btn = $('dual-camera-btn');
  // Anchor to the full-width .preview-controls bar, not btn.parentElement.
  // Since SC-29 (ee9150d) restructured the DOM, the button's immediate
  // parent is .preview-controls-camera-group -- a narrow (~96px on a
  // 393px mobile viewport) inner flex group pinned near the right edge,
  // not the full-width bar. Clamping the notice (up to 220px wide) against
  // that narrow group's own coordinate space let the clamp math produce a
  // center point past .preview-box's overflow:hidden edge, physically
  // clipping the message. .preview-controls spans the box's full width
  // (position: absolute; left: 0; right: 0) inside .preview-box, which is
  // the actual overflow:hidden ancestor doing the clipping, so anchoring
  // there keeps the whole notice inside the visible, clipped region.
  const controls = btn.closest('.preview-controls');
  if (!dualCameraNoticeEl) {
    dualCameraNoticeEl = document.createElement('div');
    dualCameraNoticeEl.className = 'dual-camera-notice';
    dualCameraNoticeEl.setAttribute('role', 'status');
    dualCameraNoticeEl.setAttribute('aria-live', 'polite');
    controls?.appendChild(dualCameraNoticeEl);
  }
  dualCameraNoticeEl.textContent = text;
  if (controls) {
    // Center the notice over the dual-camera button specifically -- its
    // position within .preview-controls shifts depending on which other
    // camera buttons are currently shown, so this is computed fresh each
    // time rather than fixed in CSS. Clamped to the controls bar's own
    // width (== the preview box's full width) so the notice can't render
    // partly or fully off-screen when the button sits near an edge on a
    // narrow phone -- offsetWidth is read AFTER textContent is set, so it
    // reflects this message's actual rendered width, not a stale one.
    const controlsRect = controls.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    const halfWidth = dualCameraNoticeEl.offsetWidth / 2;
    const center = btnRect.left - controlsRect.left + btnRect.width / 2;
    const margin = 6;
    const min = halfWidth + margin;
    const max = Math.max(min, controlsRect.width - halfWidth - margin);
    dualCameraNoticeEl.style.left = `${Math.min(Math.max(center, min), max)}px`;
  }
  window.clearTimeout(dualCameraNoticeHideTimer);
  dualCameraNoticeEl.classList.remove('dual-camera-notice-show');
  void dualCameraNoticeEl.offsetWidth;
  dualCameraNoticeEl.classList.add('dual-camera-notice-show');
  dualCameraNoticeHideTimer = window.setTimeout(() => {
    dualCameraNoticeEl?.classList.remove('dual-camera-notice-show');
  }, ms);
}

const COPY = {
  idle: { ariaLabel: 'Record', title: 'Ready when you are.', detail: 'Choose what to record, then press record.' },
  armedScreen: { ariaLabel: 'Starting recording', title: 'Choose your screen…', detail: 'Confirm a screen, window, or tab in the browser prompt.' },
  armedCamera: { ariaLabel: 'Starting recording', title: 'Starting your camera…', detail: 'One moment.' },
  countdown: { ariaLabel: 'Recording starting', title: 'Recording starts in…', detail: 'Your share link is already live.' },
  recording: { ariaLabel: 'Stop recording', title: 'Recording is live.', detail: 'Uploading continuously — safe to close after you stop.' },
  paused: { ariaLabel: 'Stop recording', title: 'Recording paused.', detail: 'Tap Resume to keep going.' },
  finishing: { ariaLabel: 'Finishing recording', title: 'Developing your take…', detail: 'Uploading the last few seconds.' },
  error: {
    ariaLabel: 'Record',
    title: "Couldn't start recording.",
    detail: 'Check your browser permissions, then press record again.',
  },
} as const;

// ---------------------------------------------------------------------------
// What-to-record toggles (replaces the old explicit 3-way mode picker).

function getCaptureMode(): CaptureMode | null {
  const screenOn = supportsDisplayMedia && $<HTMLInputElement>('toggle-screen').checked;
  const cameraOn = $<HTMLInputElement>('toggle-camera').checked;
  if (dualCameraEnabled && cameraOn) return 'dual';
  if (screenOn && cameraOn) return 'both';
  if (screenOn) return 'screen';
  if (cameraOn) return 'camera';
  return null; // neither on — caller must guard, UI prevents this via guardAtLeastOne
}

function applyCaptureModeUI(): void {
  const mode = getCaptureMode();
  const box = $('preview-box');
  box.dataset.mode = mode === 'camera' || mode === 'dual' ? 'camera' : 'screen';
  if (mode !== 'camera' && mode !== 'dual') box.style.removeProperty('--camera-aspect-ratio');
  box.dataset.previewBubble = mode === 'both' || mode === 'dual' ? 'true' : 'false';
  $('screen-mode-row').classList.toggle('hidden', !supportsDisplayMedia || !$<HTMLInputElement>('toggle-screen').checked);
  // Keep the recording canvas's grab-cursor affordance in sync even when
  // mode changes mid-recording (e.g. activating dual camera partway
  // through a camera-only recording) -- pointerIsOnBubble() itself checks
  // live capture mode regardless, so this is a cursor-styling correctness
  // fix, not a functional one, but a stale cursor is still a real bug.
  box.querySelector('.preview-canvas')?.classList.toggle('bubble-interactive', mode === 'both' || mode === 'dual');
  applyPreviewBubble();
}

function initToggleRow(): void {
  const screenT = $<HTMLInputElement>('toggle-screen');
  const cameraT = $<HTMLInputElement>('toggle-camera');
  const micT = $<HTMLInputElement>('toggle-mic');

  if (!supportsDisplayMedia) {
    prefs.captureMode = 'camera';
    prefs.cameraEnabled = true;
    savePreferences(prefs);
    $('toggle-screen-control').classList.add('hidden');
    $('select-screen-preview-btn').classList.add('hidden');
    $('select-screen-preview-control-btn').classList.add('hidden');
    $('change-screen-preview-btn').classList.add('hidden');
    $('screen-preview-video').classList.add('hidden');
    $('screen-mode-row').classList.add('hidden');
  }
  screenT.checked = supportsDisplayMedia && prefs.captureMode !== 'camera';
  cameraT.checked = !supportsDisplayMedia || prefs.captureMode === 'camera' || prefs.captureMode === 'both';
  micT.checked = prefs.microphoneEnabled;

  const persistMode = () => {
    const mode = getCaptureMode();
    if (!mode) return;
    prefs.captureMode = mode === 'dual' ? 'camera' : mode;
    prefs.cameraEnabled = cameraT.checked;
    savePreferences(prefs);
  };

  // At least one of screen/camera must stay on — revert the toggle that
  // would leave both off instead of allowing a dead-end state.
  const guardAtLeastOne = (justChanged: HTMLInputElement, other: HTMLInputElement): boolean => {
    if (allowAllCaptureOff) return false;
    if (!justChanged.checked && !other.checked) {
      justChanged.checked = true;
      setDeviceStatus('Keep at least one of screen or camera on.', 'warn');
      return true;
    }
    return false;
  };

  screenT.addEventListener('change', () => {
    if (guardAtLeastOne(screenT, cameraT)) return;
    const wasDualCamera = screenT.checked && dualCameraEnabled;
    if (wasDualCamera) dualCameraEnabled = false;
    persistMode();
    applyCaptureModeUI();
    setScreenPreviewControls(!!screenPreviewStream);
    if (screenT.checked) void startScreenPreview();
    else stopScreenPreview();
    if (wasDualCamera && cameraT.checked) void startCameraPreview();
    updateFlipCameraControl();
  });
  cameraT.addEventListener('change', () => {
    if (guardAtLeastOne(cameraT, screenT)) return;
    persistMode();
    applyCaptureModeUI();
    if (cameraT.checked) void startCameraPreview();
    else {
      dualCameraEnabled = false;
      stopCameraPreview();
    }
    updateFlipCameraControl();
  });
  micT.addEventListener('change', () => {
    prefs.microphoneEnabled = micT.checked;
    savePreferences(prefs);
    if (dualCameraEnabled) dualCameraEnabled = false;
    if (cameraT.checked) void startCameraPreview(); // rebuild the combined stream with/without audio
    applyCaptureModeUI();
    updateFlipCameraControl();
  });
  $('select-screen-preview-btn').addEventListener('click', () => {
    void startScreenPreview();
  });
  $('change-screen-preview-btn').addEventListener('click', () => {
    void startScreenPreview();
  });
  $('select-screen-preview-control-btn').addEventListener('click', () => {
    // Reused from camera-only mode too now (setScreenPreviewControls() no
    // longer requires toggle-screen to already be checked) -- dispatching
    // the checkbox's own change event, rather than calling
    // startScreenPreview() directly, is what runs applyCaptureModeUI() so
    // the mode actually flips to 'both' instead of silently starting a
    // screen stream that stays hidden behind the full-frame camera video.
    screenT.checked = true;
    screenT.dispatchEvent(new Event('change', { bubbles: true }));
  });
  $('toggle-camera-btn').addEventListener('click', () => {
    cameraT.checked = true;
    cameraT.dispatchEvent(new Event('change', { bubbles: true }));
  });
  $('flip-camera-btn').addEventListener('click', () => {
    void flipCamera();
  });
  $('dual-camera-btn').addEventListener('click', () => {
    void attemptDualCamera();
  });
  $('preview-close-btn').addEventListener('click', () => {
    if (document.body.classList.contains('is-recording') || document.body.classList.contains('is-finishing')) return;
    allowAllCaptureOff = true;
    screenT.checked = false;
    cameraT.checked = false;
    screenT.dispatchEvent(new Event('change', { bubbles: true }));
    cameraT.dispatchEvent(new Event('change', { bubbles: true }));
    allowAllCaptureOff = false;
  });

  applyCaptureModeUI();
  setScreenPreviewControls(false);
  void refreshCameraFlipAvailability();
  if (cameraT.checked) {
    // Reaching here with the toggle already checked means either a stored
    // preference or -- the common real-phone case -- supportsDisplayMedia
    // was false above and forced it on: either way this is the page-load
    // auto-start, not a user tapping the Camera toggle. Commit to the studio
    // (full-screen, mobile) layout synchronously, before the getUserMedia()
    // round trip inside startCameraPreview() below actually settles, so the
    // browser's first paint already matches the eventual state instead of
    // briefly showing the full scrollable page and then snapping into
    // full-screen (SC-40). Deliberately scoped to just this call site, not
    // startCameraPreview() itself: doing this on every interactive toggle
    // tap too would hide the toggle row (and with it Screen/Mic and "Change
    // device") the instant Camera is tapped, before any frame is even
    // showing, which breaks the expected flow of adjusting other toggles
    // right after (see the SC-34/SC-35 coverage this would otherwise break).
    cameraPreviewPending = true;
    updateStudioMode();
    void startCameraPreview();
  }
}

// ---------------------------------------------------------------------------
// Live camera preview — shown before Record is pressed, as soon as Camera
// is toggled on. Reuses acquireUserStream so it behaves like the real thing
// (same device, same permission prompt); released right before the actual
// recording acquires its own stream.

async function startCameraPreview(): Promise<void> {
  stopCameraPreviewTracks();
  try {
    const stream = await acquireUserStream({
      cameraEnabled: true,
      microphoneEnabled: $<HTMLInputElement>('toggle-mic').checked,
      cameraDeviceId: prefs.cameraDeviceId,
      cameraFacing: prefs.cameraFacing,
      microphoneDeviceId: prefs.microphoneDeviceId,
    });
    if (!stream || stream.getVideoTracks().length === 0) {
      setDeviceStatus('Camera preview unavailable — check permission or pick a different camera.', 'warn');
      return;
    }
    // The toggle may have been switched off again while permission was pending.
    if (!$<HTMLInputElement>('toggle-camera').checked) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    livePreviewStream = stream;
    const secondVideo = $<HTMLVideoElement>('camera-preview-video-2');
    secondVideo.classList.add('hidden');
    secondVideo.srcObject = null;
    applyCameraPreviewAspect(stream);
    const video = $<HTMLVideoElement>('camera-preview-video');
    video.srcObject = stream;
    video.classList.remove('hidden');
    applyPreviewBubble();
    updatePreviewPlaceholder();
    updateStudioMode();
    // enumerateDevices() can undercount/mislabel cameras before permission
    // is granted — this is commonly the FIRST successful grant (toggling
    // Camera on), so re-check now that labels/count are accurate, not just
    // via the device dialog's explicit "Allow camera & mic access" button.
    void refreshCameraFlipAvailability();
  } catch (err) {
    console.warn('[clip] camera preview failed', err);
    setDeviceStatus('Camera preview unavailable — check permission or pick a different camera.', 'warn');
  } finally {
    cameraPreviewPending = false;
    updateStudioMode();
  }
}

function stopTracks(streams: Array<MediaStream | null>): void {
  for (const stream of streams) stream?.getTracks().forEach((track) => track.stop());
}

async function acquireDualCameraStreams(): Promise<{ primary: MediaStream; secondary: MediaStream } | null> {
  let primaryMuted = false;
  let secondaryMuted = false;
  const watchTrack = (stream: MediaStream | null, setMuted: (value: boolean) => void) => {
    const track = stream?.getVideoTracks()[0];
    if (!track) return;
    const markMuted = () => setMuted(true);
    track.addEventListener('muted', markMuted, { once: true });
    track.addEventListener('ended', markMuted, { once: true });
    if (track.muted || track.readyState === 'ended') setMuted(true);
  };
  // Requesting two physical cameras at the same instant is a well-known
  // source of first-attempt hardware-contention failures on mobile: one of
  // the two getUserMedia() calls silently comes back muted/trackless while
  // the other succeeds. Acquiring sequentially — fully settling the primary
  // request before even starting the secondary one — avoids that race
  // instead of just recovering from it after the fact.
  const withTimeout = (request: Promise<MediaStream | null>): Promise<MediaStream | null> => {
    let timedOut = false;
    const timer = window.setTimeout(() => { timedOut = true; }, 1800);
    return Promise.race([
      request,
      new Promise<null>((resolve) => window.setTimeout(() => resolve(null), 1800)),
    ]).then((result) => {
      window.clearTimeout(timer);
      // A camera that finally answers after its own timeout already gave up
      // must still be stopped — otherwise a late-arriving stream leaks an
      // open track for the rest of the tab's lifetime.
      if (timedOut) void request.then((stream) => stopTracks([stream]));
      return result;
    });
  };
  const primary = await withTimeout(acquireUserStream({
    cameraEnabled: true,
    // Mirrors flipCamera()'s exact rule: during an active recording, mic
    // audio already flows from the original acquisition and is never
    // revisited by replaceCameraStream()/setDualCameraStream() (they only
    // ever touch video tracks) — requesting mic here too would acquire a
    // second, live MediaStreamTrack that nothing ever stops, leaking it for
    // the rest of the tab's lifetime. Only request it for the idle-preview
    // case, where livePreviewStream is replaced wholesale and properly
    // tracked for cleanup.
    microphoneEnabled: !active && $<HTMLInputElement>('toggle-mic').checked,
    cameraFacing: 'environment',
    microphoneDeviceId: prefs.microphoneDeviceId,
  }));
  watchTrack(primary, (value) => { primaryMuted = value; });
  if (!primary?.getVideoTracks().length) {
    stopTracks([primary]);
    // Longer than the 1800ms default: this can fire mid-recording, when the
    // full-screen mobile layout hides #device-status entirely (its usual
    // persistent home) and the user's attention is on framing/talking, not
    // a small toast in the corner.
    showToast("Dual camera isn't available on this device — try flipping between cameras instead.", 4000);
    return null;
  }
  const secondary = await withTimeout(acquireUserStream({
    cameraEnabled: true,
    microphoneEnabled: false,
    cameraFacing: 'user',
  }));
  watchTrack(secondary, (value) => { secondaryMuted = value; });
  if (!secondary?.getVideoTracks().length) {
    stopTracks([primary, secondary]);
    // Longer than the 1800ms default: this can fire mid-recording, when the
    // full-screen mobile layout hides #device-status entirely (its usual
    // persistent home) and the user's attention is on framing/talking, not
    // a small toast in the corner.
    showToast("Dual camera isn't available on this device — try flipping between cameras instead.", 4000);
    return null;
  }
  await new Promise((resolve) => window.setTimeout(resolve, 400));
  if (!primary.getVideoTracks().length || !secondary.getVideoTracks().length || primaryMuted || secondaryMuted) {
    stopTracks([primary, secondary]);
    // Longer than the 1800ms default: this can fire mid-recording, when the
    // full-screen mobile layout hides #device-status entirely (its usual
    // persistent home) and the user's attention is on framing/talking, not
    // a small toast in the corner.
    showToast("Dual camera isn't available on this device — try flipping between cameras instead.", 4000);
    return null;
  }
  return { primary, secondary };
}

async function attemptDualCamera(): Promise<void> {
  if (!hasMultipleCameras || !$<HTMLInputElement>('toggle-camera').checked) return;
  if (dualCameraEnabled) {
    dualCameraEnabled = false;
    setPreviewSwitching(true);
    if (active) await active.session.setDualCameraStream(null);
    else await startCameraPreview();
    setPreviewSwitching(false);
    applyCaptureModeUI();
    updateFlipCameraControl();
    return;
  }
  setPreviewSwitching(true);
  // stopCameraPreviewTracks() (not the full stopCameraPreview()) so the
  // current camera frame stays visibly frozen in place -- dimmed by
  // [data-switching] -- instead of tearing down to #preview-placeholder for
  // the ~2s acquireDualCameraStreams() round trip below (SC-41).
  stopCameraPreviewTracks();
  const streams = await acquireDualCameraStreams();
  if (!streams) {
    setPreviewSwitching(false);
    setDeviceStatus("Dual camera isn't available on this device — using one camera instead.", 'warn');
    showDualCameraNotice("Dual camera isn't available on this device — using one camera instead.");
    if (!active) void startCameraPreview();
    return;
  }
  try {
    if (active) {
      await active.session.replaceCameraStream(streams.primary);
      await active.session.setDualCameraStream(streams.secondary);
    } else {
      livePreviewStream = streams.primary;
      livePreviewSecondCamStream = streams.secondary;
      applyCameraPreviewAspect(streams.primary);
      const primaryVideo = $<HTMLVideoElement>('camera-preview-video');
      const secondaryVideo = $<HTMLVideoElement>('camera-preview-video-2');
      primaryVideo.srcObject = streams.primary;
      secondaryVideo.srcObject = streams.secondary;
      primaryVideo.classList.remove('hidden');
      secondaryVideo.classList.remove('hidden');
      await Promise.all([primaryVideo.play(), secondaryVideo.play()]);
      applyPreviewBubble();
      updatePreviewPlaceholder();
      updateStudioMode();
    }
    dualCameraEnabled = true;
    if (!active) stopScreenPreview();
    $<HTMLInputElement>('toggle-screen').checked = false;
    // stopScreenPreview() (called above, only in the !active branch) already
    // refreshes these buttons, but it ran BEFORE toggle-screen was unchecked
    // -- and the active-recording branch never calls it at all -- so without
    // this, a screen-preview button can stay visibly stuck showing even
    // though Screen is no longer checked.
    setScreenPreviewControls(false);
    applyCaptureModeUI();
    updateFlipCameraControl();
  } catch (err) {
    stopTracks([streams.primary, streams.secondary]);
    console.warn('[clip] dual camera activation failed', err);
    // Longer than the 1800ms default: this can fire mid-recording, when the
    // full-screen mobile layout hides #device-status entirely (its usual
    // persistent home) and the user's attention is on framing/talking, not
    // a small toast in the corner.
    showToast("Dual camera isn't available on this device — try flipping between cameras instead.", 4000);
    setDeviceStatus("Dual camera isn't available on this device — using one camera instead.", 'warn');
    showDualCameraNotice("Dual camera isn't available on this device — using one camera instead.");
    if (!active) void startCameraPreview();
  } finally {
    setPreviewSwitching(false);
  }
}

function updateFlipCameraControl(): void {
  const cameraOn = $<HTMLInputElement>('toggle-camera').checked;
  $('toggle-camera-btn').classList.toggle('hidden', cameraOn);
  $('flip-camera-btn').classList.toggle('hidden', !hasMultipleCameras || !cameraOn || dualCameraEnabled);
  const dualCameraButton = $('dual-camera-btn');
  dualCameraButton.classList.toggle('hidden', !hasMultipleCameras || !cameraOn);
  dualCameraButton.classList.toggle('active', dualCameraEnabled);
}

async function refreshCameraFlipAvailability(): Promise<void> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    hasMultipleCameras = devices.filter((device) => device.kind === 'videoinput').length >= 2;
  } catch {
    hasMultipleCameras = false;
  }
  updateFlipCameraControl();
}

async function flipCamera(): Promise<void> {
  if (flippingCamera || dualCameraEnabled || !$<HTMLInputElement>('toggle-camera').checked) return;
  flippingCamera = true;
  setPreviewSwitching(true);
  const nextFacing = (prefs.cameraFacing ?? 'user') === 'user' ? 'environment' : 'user';
  prefs.cameraFacing = nextFacing;
  savePreferences(prefs);
  let replacementStream: MediaStream | null = null;
  try {
    const stream = await acquireUserStream({
      cameraEnabled: true,
      microphoneEnabled: !active && $<HTMLInputElement>('toggle-mic').checked,
      cameraFacing: nextFacing,
      microphoneDeviceId: prefs.microphoneDeviceId,
    });
    if (!stream?.getVideoTracks().length) throw new Error('The other camera is unavailable.');
    replacementStream = stream;
    if (active) {
      await active.session.replaceCameraStream(stream);
    } else if (livePreviewStream) {
      const video = $<HTMLVideoElement>('camera-preview-video');
      const oldStream = livePreviewStream;
      video.srcObject = stream;
      try {
        await video.play();
      } catch (err) {
        video.srcObject = oldStream;
        void video.play();
        throw err;
      }
      livePreviewStream = stream;
      applyCameraPreviewAspect(stream);
      applyPreviewBubble();
      oldStream.getTracks().forEach((track) => track.stop());
    } else {
      stream.getTracks().forEach((track) => track.stop());
      await startCameraPreview();
    }
    setDeviceStatus(`Using the ${nextFacing === 'user' ? 'front' : 'rear'} camera.`, 'ok');
  } catch (err) {
    replacementStream?.getTracks().forEach((track) => track.stop());
    console.warn('[clip] camera flip failed', err);
    setDeviceStatus('Could not switch cameras. Your current camera is still active.', 'warn');
  } finally {
    flippingCamera = false;
    setPreviewSwitching(false);
  }
}

function stopCameraPreviewTracks(): void {
  // Also called from the top of startCameraPreview() itself, which sets this
  // back to true right after -- reset here mainly so an in-flight preview
  // getting cancelled (camera toggled off again before it resolves) can't
  // leave the studio layout stuck on once its own `finally` fires.
  cameraPreviewPending = false;
  if (livePreviewStream) {
    livePreviewStream.getTracks().forEach((t) => t.stop());
    livePreviewStream = null;
  }
  if (livePreviewSecondCamStream) {
    livePreviewSecondCamStream.getTracks().forEach((t) => t.stop());
    livePreviewSecondCamStream = null;
  }
}

function stopCameraPreview(): void {
  stopCameraPreviewTracks();
  const video = $<HTMLVideoElement>('camera-preview-video');
  video.classList.add('hidden');
  video.srcObject = null;
  const secondVideo = $<HTMLVideoElement>('camera-preview-video-2');
  secondVideo.classList.add('hidden');
  secondVideo.srcObject = null;
  updatePreviewPlaceholder();
  updateStudioMode();
}

function applyCameraPreviewAspect(stream: MediaStream): void {
  const settings = stream.getVideoTracks()[0]?.getSettings();
  if (settings?.width && settings.height) {
    $('preview-box').style.setProperty('--camera-aspect-ratio', String(settings.width / settings.height));
  }
}

function updatePreviewPlaceholder(): void {
  const hasPreview = !!screenPreviewStream || !!livePreviewStream || !!$('preview-box').querySelector('.preview-canvas');
  $('preview-placeholder').classList.toggle('hidden', hasPreview || document.body.classList.contains('is-recording'));
}

/** SC-41: marks a camera SWITCH (flip, dual-camera toggle, device-dialog
 * camera/mic change) as in progress, purely for the [data-switching] CSS
 * (dim + pulsing badge). Callers deliberately skip the full
 * stopCameraPreview() teardown during these gaps, so the existing video's
 * last frame stays on screen the whole time instead of tearing down to
 * #preview-placeholder — this just makes that gap read as intentional. */
function setPreviewSwitching(switching: boolean): void {
  $('preview-box').dataset.switching = switching ? 'true' : 'false';
}

/** Reacquires the live camera preview (device-dialog camera/mic pick) while
 * flagging it as a source SWITCH rather than a fresh start — see
 * setPreviewSwitching(). startCameraPreview() itself only stops the old
 * stream's tracks (never hides the video or clears its srcObject), so the
 * old frame stays frozen on screen for the CSS dim/badge to sit on top of. */
async function switchCameraPreview(): Promise<void> {
  setPreviewSwitching(true);
  try {
    await startCameraPreview();
  } finally {
    setPreviewSwitching(false);
  }
}

function updateStudioMode(): void {
  // cameraPreviewPending covers the getUserMedia() round trip itself so the
  // full-screen studio layout is committed to immediately, not only once the
  // stream lands (SC-40) — see its doc comment.
  const hasPreview = !!screenPreviewStream || !!livePreviewStream || cameraPreviewPending || !!$('preview-box').querySelector('.preview-canvas');
  const isRecordingOrFinishing = document.body.classList.contains('is-recording') || document.body.classList.contains('is-finishing');
  const isStudio = hasPreview || isRecordingOrFinishing || recordingStarting;
  const enteringStudio = isStudio && !document.body.classList.contains('is-studio');
  document.body.classList.toggle('is-studio', isStudio);
  $('preview-close-btn').classList.toggle('hidden', !hasPreview || isRecordingOrFinishing);
  // Entering studio mode resizes #preview-box via a CSS class (position:fixed
  // + inset:0 + height:100dvh) -- these properties are NOT in .preview-box's
  // transition list (only aspect-ratio/max-width are, confirmed by reading
  // style.css), so they snap to their final size synchronously and a
  // geometry read immediately after the class toggle is safe, no rAF/delay
  // needed. Without this, the bubble (positioned by whatever
  // applyPreviewBubble() call happened to run just before this one) stays
  // frozen at coordinates computed against the OLD, much smaller box.
  if (enteringStudio) applyPreviewBubble();
}

function setScreenPreviewControls(active: boolean): void {
  if (!supportsDisplayMedia) return;
  const canSelect =
    !active &&
    !document.body.classList.contains('is-recording') &&
    !document.body.classList.contains('is-finishing') &&
    control.currentState !== 'armed';
  $('select-screen-preview-btn').classList.toggle('hidden', !$<HTMLInputElement>('toggle-screen').checked);
  $('select-screen-preview-control-btn').classList.toggle('hidden', !canSelect);
  $('change-screen-preview-btn').classList.toggle('hidden', !active);
}

function stopScreenPreview(): void {
  if (screenPreviewStream) {
    screenPreviewStream.getTracks().forEach((track) => track.stop());
    screenPreviewStream = null;
  }
  const video = $<HTMLVideoElement>('screen-preview-video');
  video.classList.add('hidden');
  video.srcObject = null;
  setScreenPreviewControls(false);
  updatePreviewPlaceholder();
  updateStudioMode();
}

function takeScreenPreview(): MediaStream | null {
  const stream = screenPreviewStream;
  screenPreviewStream = null;
  const video = $<HTMLVideoElement>('screen-preview-video');
  video.classList.add('hidden');
  video.srcObject = null;
  setScreenPreviewControls(false);
  updateStudioMode();
  return stream;
}

async function startScreenPreview(): Promise<void> {
  if (!supportsDisplayMedia) return;
  stopScreenPreview();
  try {
    const stream = await acquireDisplayStream({
      displaySurface: DISPLAY_SURFACE[prefs.displayModePreference ?? 'screen'],
    });
    if (!$<HTMLInputElement>('toggle-screen').checked) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    screenPreviewStream = stream;
    const video = $<HTMLVideoElement>('screen-preview-video');
    video.srcObject = stream;
    video.classList.remove('hidden');
    setScreenPreviewControls(true);
    updatePreviewPlaceholder();
    updateStudioMode();
    stream.getVideoTracks()[0]?.addEventListener('ended', () => {
      if (screenPreviewStream !== stream) return;
      stopScreenPreview();
      setDeviceStatus('Screen sharing ended. Select a screen to preview another.', 'warn');
    });
  } catch (err) {
    console.info('[clip] screen preview selection cancelled or denied', err);
    updatePreviewPlaceholder();
    updateStudioMode();
  }
}

// ---------------------------------------------------------------------------
// Device dialog (popover, opened on demand — "only needed once")

interface PopulateResult {
  selectedId: string | undefined;
  fallbackHappened: boolean;
  fallbackLabel?: string;
}

async function populateDeviceSelect(
  select: HTMLSelectElement,
  kind: 'videoinput' | 'audioinput',
  storedId: string | undefined,
): Promise<PopulateResult> {
  const devices = await listDevices(kind).catch(() => []);
  select.innerHTML = '';
  if (devices.length === 0) {
    const opt = document.createElement('option');
    opt.value = '';
    opt.textContent = kind === 'videoinput' ? 'No camera detected' : 'No microphone detected';
    opt.disabled = true;
    select.appendChild(opt);
    select.value = '';
    select.disabled = true;
    return { selectedId: undefined, fallbackHappened: !!storedId };
  }
  select.disabled = false;
  for (const d of devices) {
    const opt = document.createElement('option');
    opt.value = d.deviceId;
    opt.textContent = d.label;
    select.appendChild(opt);
  }
  if (storedId && devices.some((d) => d.deviceId === storedId)) {
    select.value = storedId;
    return { selectedId: storedId, fallbackHappened: false };
  }
  select.value = devices[0].deviceId;
  return { selectedId: devices[0].deviceId, fallbackHappened: !!storedId, fallbackLabel: devices[0].label };
}

function persistFromSelects(camSelect: HTMLSelectElement, micSelect: HTMLSelectElement): void {
  prefs.cameraDeviceId = camSelect.value || undefined;
  prefs.microphoneDeviceId = micSelect.value || undefined;
  savePreferences(prefs);
}

async function initDeviceDialog(): Promise<void> {
  const screenModeSelect = $<HTMLSelectElement>('screen-mode');
  screenModeSelect.value = prefs.displayModePreference ?? 'screen';
  screenModeSelect.addEventListener('change', () => {
    prefs.displayModePreference = screenModeSelect.value as DisplayModePreference;
    savePreferences(prefs);
  });

  const camSelect = $<HTMLSelectElement>('camera-select');
  const micSelect = $<HTMLSelectElement>('mic-select');

  const [camResult, micResult] = await Promise.all([
    populateDeviceSelect(camSelect, 'videoinput', prefs.cameraDeviceId),
    populateDeviceSelect(micSelect, 'audioinput', prefs.microphoneDeviceId),
  ]);
  prefs.cameraDeviceId = camResult.selectedId;
  prefs.microphoneDeviceId = micResult.selectedId;
  savePreferences(prefs);

  const fallbackMessages: string[] = [];
  if (camResult.fallbackHappened && camResult.selectedId) {
    fallbackMessages.push(`Your saved camera is no longer available. Switched to "${camResult.fallbackLabel}".`);
  }
  if (micResult.fallbackHappened && micResult.selectedId) {
    fallbackMessages.push(`Your saved microphone is no longer available. Switched to "${micResult.fallbackLabel}".`);
  }
  if (fallbackMessages.length > 0) {
    fallbackPending = true;
    control.setDisabled(true);
    $('fallback-text').textContent = fallbackMessages.join(' ');
    $('fallback-confirm').classList.add('active');
    $('fallback-confirm-btn').addEventListener(
      'click',
      () => {
        fallbackPending = false;
        $('fallback-confirm').classList.remove('active');
        control.setDisabled(false);
        announce.textContent = 'Device change confirmed.';
      },
      { once: true },
    );
  }

  camSelect.addEventListener('change', () => {
    persistFromSelects(camSelect, micSelect);
    prefs.cameraFacing = undefined;
    savePreferences(prefs);
    setDeviceStatus('Camera preference saved.', 'ok');
    if ($<HTMLInputElement>('toggle-camera').checked) void switchCameraPreview();
  });
  micSelect.addEventListener('change', () => {
    persistFromSelects(camSelect, micSelect);
    setDeviceStatus('Microphone preference saved.', 'ok');
    if ($<HTMLInputElement>('toggle-camera').checked) void switchCameraPreview();
  });

  $('allow-devices-btn').addEventListener('click', async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      stream.getTracks().forEach((t) => t.stop());
      await populateDeviceSelect(camSelect, 'videoinput', prefs.cameraDeviceId);
      await populateDeviceSelect(micSelect, 'audioinput', prefs.microphoneDeviceId);
      await refreshCameraFlipAvailability();
      setDeviceStatus('Camera and microphone are ready to choose by name.', 'ok');
      if ($<HTMLInputElement>('toggle-camera').checked) void startCameraPreview();
    } catch (err) {
      console.warn('[clip] device permission priming denied', err);
      setDeviceStatus("Camera/microphone permission wasn't granted. You can still record your screen.", 'warn');
    }
  });

  const dialog = $<HTMLDialogElement>('device-dialog');
  $('device-change-btn').addEventListener('click', () => dialog.showModal());
  $('device-dialog-done').addEventListener('click', () => dialog.close());
}

navigator.mediaDevices?.addEventListener?.('devicechange', () => {
  void populateDeviceSelect($<HTMLSelectElement>('camera-select'), 'videoinput', prefs.cameraDeviceId);
  void populateDeviceSelect($<HTMLSelectElement>('mic-select'), 'audioinput', prefs.microphoneDeviceId);
});

// ---------------------------------------------------------------------------
// Library (static-thumbnail tiles) + recovery
//
// SC-115: cards used to render a real <video preload="metadata"> per tile,
// which -- even with preload=metadata -- makes the browser issue a genuine
// network request (a Range read against R2 via GET .../media) for every
// recording just to draw a list. Cards now render a plain <img> pointed at
// the small static JPEG data URI captured once client-side at recording-stop
// time (see captureThumbnailDataUri() above) and returned inline by the list
// endpoint itself -- rendering N cards costs exactly one network request
// total (the list fetch below), same as it always did for the text-only
// fields.

function libraryPreviewLabel(status: string): string {
  switch (status) {
    case 'recording': return 'Recording in progress';
    case 'processing': return 'Preparing preview';
    case 'ready': return 'Ready to watch';
    case 'failed': return 'Preview unavailable';
    case 'expired': return 'Recording unavailable';
    default: return 'Preview unavailable';
  }
}

function libraryStatusLabel(status: string): string {
  switch (status) {
    case 'recording': return 'Recording live';
    case 'processing': return 'Preparing clip';
    case 'ready': return 'Ready to watch';
    case 'failed': return 'Recording failed';
    case 'expired': return 'Clip expired';
    default: return 'Status unavailable';
  }
}

const KNOWN_LIBRARY_STATUSES = new Set(['recording', 'processing', 'ready', 'failed', 'expired']);
let libraryHasLoaded = false;

function setLibraryFeedback(message: string): void {
  const feedback = $('library-feedback');
  feedback.textContent = message;
  feedback.classList.toggle('hidden', !message);
  $('library-retry-btn').classList.toggle('hidden', !message);
}

async function refreshLibrary(): Promise<void> {
  const list = $('library-list');
  list.setAttribute('aria-busy', 'true');
  try {
    const res = await fetch(groupSlug ? `/api/groups/${encodeURIComponent(groupSlug)}/recordings` : '/api/recordings');
    if (res.status === 401) {
      location.reload(); // session expired — back to the auth gate
      return;
    }
    if (!res.ok) throw new Error(`Library request failed with ${res.status}`);
    const body = (await res.json()) as {
      recordings: {
        slug: string;
        title: string | null;
        status: string;
        createdAt: string;
        durationS: number | null;
        viewCount: number;
        bytesUploaded: number;
        uploaderName?: string | null;
        /** Small JPEG data URI (see captureThumbnailDataUri()), or null for a
         * recording that has none yet (still recording/processing, failed
         * before stop, or predates this feature). */
        thumbnail?: string | null;
      }[];
    };
    list.innerHTML = '';
    libraryHasLoaded = true;
    setLibraryFeedback('');
    if (body.recordings.length === 0) {
      list.innerHTML = `<li class="library-state library-empty">
        <span class="library-empty-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="30" height="30"><rect x="3" y="6" width="14" height="12" rx="2" fill="none" stroke="currentColor" stroke-width="2"/><path d="M17 10.5l4-2.3v7.6l-4-2.3" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>
        </span>
        <span><strong>Your first clip starts here.</strong><span>Choose what to show above, then press record. Your share link appears right away.</span></span>
        <a class="btn-secondary library-empty-action" href="#record-btn">Go to record</a>
      </li>`;
      return;
    }
    for (const r of body.recordings) {
      const li = document.createElement('li');
      li.className = 'library-tile';
      const dur = r.durationS !== null ? fmtClock(r.durationS) : '—';
      const previewLabel = libraryPreviewLabel(r.status);
      const status = KNOWN_LIBRARY_STATUSES.has(r.status) ? r.status : 'unknown';
      const statusLabel = libraryStatusLabel(r.status);
      const views = `${r.viewCount} ${r.viewCount === 1 ? 'view' : 'views'}`;
      // A broken/corrupt data URI (never observed, but img onerror is cheap
      // insurance) falls back to the same placeholder tile as "no thumbnail".
      const thumbInner = r.thumbnail
        ? `<img class="library-thumb-img" src="${escapeHtml(r.thumbnail)}" alt="" loading="lazy" decoding="async" onerror="this.remove(); this.nextElementSibling?.classList.remove('hidden');">
           <span class="library-thumb-fallback hidden">
             <svg viewBox="0 0 24 24" width="32" height="32" aria-hidden="true"><rect x="3" y="6" width="14" height="12" rx="2" fill="none" stroke="currentColor" stroke-width="2"/><path d="M17 10.5l4-2.3v7.6l-4-2.3" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>
             <span>Preview unavailable</span>
           </span>`
        : `<span class="library-thumb-fallback">
             <svg viewBox="0 0 24 24" width="32" height="32" aria-hidden="true"><rect x="3" y="6" width="14" height="12" rx="2" fill="none" stroke="currentColor" stroke-width="2"/><path d="M17 10.5l4-2.3v7.6l-4-2.3" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>
             <span>${previewLabel}</span>
           </span>`;
      li.innerHTML = `
        <a href="/w/${encodeURIComponent(r.slug)}" class="library-link">
          <span class="library-thumb">
            ${thumbInner}
            <span class="status-dot status-${status}" title="${statusLabel}" aria-hidden="true"></span>
          </span>
          <span class="library-body">
            <span class="library-title">${r.title ? escapeHtml(r.title) : 'Untitled recording'}</span>
            <span class="library-meta">${dur} · ${fmtBytes(r.bytesUploaded)} · ${views}</span>
            <span class="sr-only">Status: ${statusLabel}.</span>
            ${groupSlug ? `<span class="library-attribution">by ${escapeHtml(r.uploaderName || 'Unknown')}</span>` : ''}
          </span>
        </a>`;
      list.appendChild(li);
    }
  } catch (err) {
    console.warn('[clip] library refresh failed', err);
    setLibraryFeedback('Your library could not refresh. Your recordings are still safe.');
    if (!libraryHasLoaded) {
      list.innerHTML = `<li class="library-state library-error">
        <span class="library-error-icon" aria-hidden="true">!</span>
        <span><strong>Could not load your library.</strong><span>Check your connection, then try again.</span></span>
      </li>`;
    }
  } finally {
    list.setAttribute('aria-busy', 'false');
  }
}

function setHudStats(stats: UploaderStats): void {
  $('hud-parts').textContent = String(stats.partsUploaded);
  $('hud-uploaded').textContent = fmtBytes(stats.bytesUploaded);
  $('hud-buffered').textContent = fmtBytes(stats.bytesBuffered + stats.partsQueued * partSize);
  const el = $('hud-inflight');
  el.textContent = stats.partsInFlight > 0 ? `${stats.partsInFlight} uploading` : 'idle';
  el.dataset.state = stats.partsInFlight > 0 ? 'busy' : 'idle';
}

function showPreview(canvas: HTMLCanvasElement): void {
  const box = $('preview-box');
  $('preview-placeholder').classList.add('hidden');
  canvas.classList.add('preview-canvas');
  canvas.classList.toggle('bubble-interactive', getCaptureMode() === 'both' || getCaptureMode() === 'dual');
  bindBubbleInteractions(canvas);
  box.appendChild(canvas);
  updateStudioMode();
}

function resetPreview(): void {
  const box = $('preview-box');
  box.querySelector('.preview-canvas')?.remove();
  bubbleSession = null;
  $('preview-placeholder').classList.remove('hidden');
  updateStudioMode();
}

// ---------------------------------------------------------------------------
// Recording flow: native display picker (unless camera-only) -> camera/mic
// -> mint link -> 3-2-1 countdown (preview already visible) -> start encoder.

function detachOrientationListener(): void {
  removeOrientationListener?.();
  removeOrientationListener = null;
}

function attachOrientationListener(): void {
  detachOrientationListener();
  const notify = () => {
    if (active) showToast("Rotating your device won't change this recording — the orientation was locked when you started.");
  };
  if (typeof screen !== 'undefined' && screen.orientation) {
    screen.orientation.addEventListener('change', notify);
    removeOrientationListener = () => screen.orientation.removeEventListener('change', notify);
    return;
  }
  const query = window.matchMedia('(orientation: portrait)');
  query.addEventListener('change', notify);
  removeOrientationListener = () => query.removeEventListener('change', notify);
}

// ---------------------------------------------------------------------------
// SC-17 follow-up: dedicated live-session heartbeat. See HEARTBEAT_PING_MS's
// doc comment above for why this exists (the server's staleness sweep used
// to rely solely on part-upload cadence, which a low-motion recording could
// miss for well over the old threshold) and src/worker/index.ts's PUT
// .../:slug/heartbeat + HEARTBEAT_STALE_MS for the server side.

/** Starts the HEARTBEAT_PING_MS ping timer for a just-begun recording
 * session (see sendHeartbeat() below). Deliberately keeps pinging through
 * pause: the creator's browser/tab is exactly as alive while paused as
 * while actively encoding (pause only calls MediaRecorder.pause() -- see
 * recorder.ts -- nothing about the page, network, or upload connection
 * changes), and a paused session is exactly as "not abandoned" as a
 * recording one. This matches how the rest of the app already treats
 * pause: the beforeunload guard in initApp() and setRecordingTab() both
 * keep treating a paused session as still "in the middle of recording", not
 * as something already wound down. Stopping the heartbeat on pause would
 * let a legitimately paused (but very much alive) creator get swept into
 * 'failed' by the server the same way the original low-motion bug did --
 * exactly the failure mode this whole fix exists to close off. */
function startHeartbeat(slug: string): number {
  return window.setInterval(() => void sendHeartbeat(slug), HEARTBEAT_PING_MS);
}

// Two independent adversarial reviews of the ping redesign above (same
// session) converged on the same real gap: a bare setInterval() has zero
// Page Visibility awareness, and browsers throttle background-tab timers
// (Chrome's Intensive Wake-Up Throttling is documented to be able to align
// a hidden tab's non-exempt timers to roughly once a minute after ~5
// minutes hidden; other browsers have their own versions). Whether Clip's
// architecture -- MediaRecorder + <canvas> + WebAudio, entirely local, no
// RTCPeerConnection -- gets any of the exemptions browsers grant WebRTC or
// audible-audio pages is NOT confirmed either way (unable to check current
// docs this session); src/worker/index.ts's HEARTBEAT_STALE_MS comment has
// the full reasoning and treats it as unconfirmed rather than assuming an
// exemption. Backgrounding the recording tab on purpose -- to share/work in
// a *different* window while Clip keeps capturing -- is an expected screen-
// recording flow, not an edge case, so this can't be dismissed as unlikely.
//
// This listener is the direct, unambiguously-correct half of the fix: the
// instant the tab becomes visible again, fire a heartbeat immediately
// instead of waiting for the next (possibly throttled, possibly minutes-
// delayed) interval tick. It does NOT fix the harder, unconfirmed half --
// a recording that stays backgrounded for its *entire* duration still
// depends on however often the browser lets a hidden tab's timer fire --
// that's what HEARTBEAT_STALE_MS's wide server-side grace margin is for.
// Registered once, globally (matches the navigator.mediaDevices
// 'devicechange' listener earlier in this file): cheap to leave attached at
// all times, and simplest to reason about -- it only ever does anything
// while `active` is set, i.e. while a heartbeat timer actually exists to
// catch up.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && active) void sendHeartbeat(active.slug);
});

/** Best-effort: a failed request here just gets retried on the next tick
 * (the part-upload heartbeat bump is still in place server-side as a
 * second signal, see src/worker/index.ts), so network errors are swallowed,
 * not surfaced -- a single dropped ping isn't news. A 409 response is
 * different: it means the server has ALREADY moved this recording out of
 * 'recording' without the client doing anything (almost always the cron
 * sweep declaring it abandoned) -- that outcome must not stay silent, so it
 * hands off to abandonRecordingDueToServerStatus() instead of letting the
 * user keep believing they're recording into a session the server already
 * closed the book on. */
async function sendHeartbeat(slug: string): Promise<void> {
  try {
    const res = await fetch(`/api/recordings/${slug}/heartbeat`, { method: 'PUT' });
    if (res.status === 409) {
      const body = (await res.json().catch(() => null)) as { status?: string } | null;
      abandonRecordingDueToServerStatus(body?.status);
    }
  } catch (err) {
    console.warn('[clip] heartbeat ping failed (will retry next tick)', err);
  }
}

/** Called when a heartbeat ping (see sendHeartbeat() above) discovers the
 * server already moved this recording out of 'recording' status without
 * the client itself calling stopRecording() -- in practice, almost always
 * the cron sweep (src/worker/index.ts's scheduled()) declaring the session
 * abandoned because heartbeats stopped landing (crashed tab, lost
 * connection, a backgrounded mobile browser that got suspended, etc.).
 *
 * Deliberately does NOT attempt uploader.finish(): this 409 already tells
 * us both PUT .../parts/:n and POST .../complete would themselves 409
 * against that same status, so retrying them would only burn ~15s of
 * exponential backoff (see uploader.ts's uploadWithRetry) before landing at
 * essentially this same recovery state. The local encoder is stopped and
 * the IndexedDB journal is left intact (already durable -- addChunk()
 * journals before upload even attempts a network call), so reloading still
 * offers to recover it via checkRecovery(), same as any other interrupted
 * session. */
function abandonRecordingDueToServerStatus(status: string | undefined): void {
  if (!active) return; // already stopped locally (a stale ping racing a real stop) -- nothing to reconcile
  const rec = active;
  active = null;
  window.clearInterval(rec.timerHandle);
  window.clearTimeout(rec.capHandle);
  window.clearInterval(rec.heartbeatHandle);
  detachOrientationListener();
  rec.session.stop().catch(() => {});
  document.body.classList.remove('is-recording', 'is-finishing');
  updateStudioMode();
  setScreenPreviewControls(!!screenPreviewStream);
  setPauseResumeControl(false);
  resetPreview();
  setRecordingTab(false);
  control.setState('error', {
    ...COPY.error,
    title: 'Recording stopped.',
    detail: 'The server marked this recording inactive — your chunks are journaled locally, reload to check it.',
  });
  control.setDisabled(false);
  const label = status && status !== 'recording' ? status : 'inactive';
  showToast(`This recording was marked ${label} by the server, as if it had disconnected. Reload to check it.`, 6000);
  void refreshLibrary();
}

async function beginFlow(): Promise<void> {
  if (fallbackPending) return;
  const mode = getCaptureMode();
  if (!mode) {
    setDeviceStatus('Turn on Screen or Camera to record.', 'error');
    return;
  }
  const camSelect = $<HTMLSelectElement>('camera-select');
  if ((mode === 'camera' || mode === 'dual') && (!camSelect.value || camSelect.disabled)) {
    // Bug (mobile "Record sometimes doesn't work"): #device-status is hidden
    // entirely under is-studio (see style.css's body.is-studio rules) -- and
    // by the time a user can even reach this guard, a live camera preview is
    // already up, which is exactly when is-studio is active. Without a
    // toast (never hidden by is-studio, same pattern as the dual-camera
    // fallback notice below), this guard fired with ZERO visible feedback:
    // the button just silently did nothing.
    setDeviceStatus('No camera detected — plug one in, or turn on Screen instead.', 'error');
    showToast('No camera detected — plug one in, or turn on Screen instead.', 4000);
    return;
  }

  control.setDisabled(true);
  // Commit to studio mode before releasing the live preview below -- see
  // recordingStarting's doc comment for why this has to happen first.
  recordingStarting = true;
  updateStudioMode();
  stopCameraPreview(); // release the preview stream; the recording acquires its own

  let screenStream: MediaStream | null = null;
  let camStream: MediaStream | null = null;
  let secondCamStream: MediaStream | null = null;
  if (mode !== 'camera' && mode !== 'dual') {
    control.setState('armed', COPY.armedScreen);
    screenStream = takeScreenPreview();
    if (!screenStream) {
      try {
        screenStream = await acquireDisplayStream({
          displaySurface: DISPLAY_SURFACE[prefs.displayModePreference ?? 'screen'],
        });
      } catch (err) {
        console.info('[clip] display selection cancelled or denied', err);
        recordingStarting = false;
        control.setState('idle', COPY.idle);
        control.setTimerText('00:00');
        control.setDisabled(false);
        if ($<HTMLInputElement>('toggle-camera').checked) void startCameraPreview();
        updateStudioMode();
        return;
      }
    }
  } else {
    control.setState('armed', COPY.armedCamera);
  }

  try {
    const wantedCamera = mode !== 'screen';
    const wantedMic = prefs.microphoneEnabled;
    if (mode === 'dual') {
      const dualStreams = await acquireDualCameraStreams();
      if (!dualStreams) throw new Error('Dual camera is unavailable.');
      camStream = dualStreams.primary;
      secondCamStream = dualStreams.secondary;
    } else {
      camStream = await acquireUserStream({
        cameraEnabled: wantedCamera,
        microphoneEnabled: wantedMic,
        cameraDeviceId: prefs.cameraDeviceId,
        cameraFacing: prefs.cameraFacing,
        microphoneDeviceId: prefs.microphoneDeviceId,
      });
    }
    if ((mode === 'camera' || mode === 'dual') && camStream) applyCameraPreviewAspect(camStream);
    const gotCamera = !!camStream?.getVideoTracks().length;
    const gotMic = !!camStream?.getAudioTracks().length;
    if ((mode === 'camera' || mode === 'dual') && !gotCamera) {
      throw new Error('Camera permission is required for camera-only recording.');
    }
    if (mode !== 'camera' && ((wantedCamera && !gotCamera) || (wantedMic && !gotMic))) {
      setDeviceStatus('Continuing without it — camera/microphone permission was not granted.', 'warn');
    }

    // Must match prepareRecording()'s own haveAudio computation exactly
    // (client/recorder.ts) — this mimeType is what a live viewer's
    // MediaSource.addSourceBuffer() uses, so it has to describe the real
    // composed stream, not just whether the mic was requested.
    const hasAudio = !!camStream?.getAudioTracks().length || !!screenStream?.getAudioTracks().length;
    const container = detectContainer(hasAudio);
    const mimeType = detectMimeType(hasAudio);
    const title = ($<HTMLInputElement>('rec-title')).value.trim();
    const description = ($<HTMLTextAreaElement>('rec-description')).value.trim();
    const res = await fetch('/api/recordings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ container, mimeType, title: title || undefined, description: description || undefined, groupSlug: groupSlug || undefined }),
    });
    if (res.status === 401) {
      location.reload(); // session expired mid-flow — back to the auth gate
      return;
    }
    if (!res.ok) throw new Error(`create failed: HTTP ${res.status}`);
    const { slug, watchUrl } = (await res.json()) as { slug: string; watchUrl: string };

    await journal.createSession({ slug, container, createdAt: Date.now(), partSize, completed: false });
    const uploader = new PartUploader(slug, journal, partSize);
    uploader.onStats = setHudStats;

    // Camera dimensions are intentionally captured here and fixed for this recording: live re-orientation is not reliable across browsers.
    const session = prepareRecording(screenStream, camStream, secondCamStream, {
      captureMode: mode,
      videoBitsPerSecond: vbps,
      noiseOverlay,
      onChunk: (blob) => void uploader.addChunk(blob).catch((err) => console.error('[clip] chunk error', err)),
      onError: (err) => {
        console.error('[clip] recorder error', err);
        void stopRecording();
      },
    });
    bubbleSession = session;
    syncBubble();
    session.onExternalStop(() => {
      cancelCountdown = true;
      void stopRecording();
    });

    // E2E hook: exposes what the recorder actually acquired, so tests can
    // assert on real capability (e.g. "mic audio was really captured")
    // instead of guessing from UI state alone.
    (window as unknown as { __clipDebug?: unknown }).__clipDebug = {
      captureMode: mode,
      hasCamera: session.hasCamera,
      hasAudio: session.hasAudio,
      screenTrackId: screenStream?.getVideoTracks()[0]?.id ?? null,
    };

    // Live preview is visible from here — during the countdown, not just
    // once recording actually starts.
    showPreview(session.canvas);

    // Watch link is live before capture actually begins.
    const link = $<HTMLAnchorElement>('hud-watch-link');
    link.href = watchUrl;
    link.textContent = watchUrl;

    cancelCountdown = false;
    control.setState('armed', COPY.countdown);
    const completed = await countdown(control, COUNTDOWN_S, () => cancelCountdown);
    if (!completed) {
      recordingStarting = false;
      session.stop().catch(() => {});
      dualCameraEnabled = false;
      updateFlipCameraControl();
      resetPreview();
      control.setState('idle', COPY.idle);
      control.setTimerText('00:00');
      control.setDisabled(false);
      if ($<HTMLInputElement>('toggle-camera').checked) void startCameraPreview();
      if ($<HTMLInputElement>('toggle-screen').checked) void startScreenPreview();
      return;
    }

    session.begin();
    recordingStarting = false;
    document.body.classList.add('is-recording');
    updateStudioMode();
    // setScreenPreviewControls()'s own canSelect logic already excludes
    // is-recording -- but that's only evaluated when this function actually
    // runs, and nothing re-ran it when the class above just changed, so the
    // screen-select icon stayed visible/clickable throughout a camera-only
    // recording (found during SC-42's control-row review).
    setScreenPreviewControls(!!screenPreviewStream);
    control.setState('recording', COPY.recording);
    setPauseResumeControl(true);
    setRecordingTab(true, '00:00');

    let displayedClock = '00:00';
    const timerHandle = window.setInterval(() => {
      const clock = fmtClock(session.elapsedS());
      // Keep the short cadence so the 60-minute cap and pause state remain
      // responsive, but a MM:SS display changes only once a second. Avoid
      // repeatedly writing the same HUD/control/title values during active
      // capture, where canvas composition and encoding already compete for
      // the main thread.
      if (clock === displayedClock) return;
      displayedClock = clock;
      $('hud-timer').textContent = clock;
      control.setTimerText(clock);
      setRecordingTab(true, clock, session.isPaused());
    }, 250);
    const capHandle = window.setTimeout(() => {
      console.warn('[clip] 1h cap reached — auto-stopping');
      void stopRecording();
    }, MAX_DURATION_S * 1000);
    const heartbeatHandle = startHeartbeat(slug);

    active = { slug, watchUrl, session, uploader, timerHandle, capHandle, heartbeatHandle };
    attachOrientationListener();
    control.setDisabled(false);
  } catch (err) {
    console.error('[clip] failed to start recording', err);
    recordingStarting = false;
    for (const t of screenStream?.getTracks() ?? []) t.stop();
    for (const t of camStream?.getTracks() ?? []) t.stop();
    for (const t of secondCamStream?.getTracks() ?? []) t.stop();
    dualCameraEnabled = false;
    updateFlipCameraControl();
    resetPreview();
    control.setState('error', { ...COPY.error, detail: (err as Error).message || COPY.error.detail });
    setDeviceStatus(`Could not start recording: ${(err as Error).message}`, 'error');
    control.setDisabled(false);
    document.body.classList.remove('is-recording');
    updateStudioMode();
    detachOrientationListener();
    setRecordingTab(false);
    // Bug ("Record sometimes doesn't work" on mobile): both #device-status
    // and .record-status (control.setState()'s title/detail text, the ONLY
    // other feedback set above) are hidden entirely under is-studio (see
    // style.css). That's normally fine while a preview is up -- but the two
    // restart calls right below are exactly what flips is-studio back ON
    // (their success re-adds a live camera/screen preview), typically well
    // under 100ms later. Confirmed live: the error text was in the DOM for
    // under 100ms before is-studio re-hid it, leaving a user on what looks
    // like a perfectly normal, ready-to-record preview with zero indication
    // Record just failed or why. A toast (appended to <body>, never in
    // is-studio's hidden list -- same pattern as the dual-camera fallback
    // notice elsewhere in this file) survives that restart.
    showToast(`Could not start recording: ${(err as Error).message}`, 4500);
    if ($<HTMLInputElement>('toggle-camera').checked) void startCameraPreview();
    if ($<HTMLInputElement>('toggle-screen').checked) void startScreenPreview();
  }
}

async function stopRecording(): Promise<void> {
  if (!active) return;
  const rec = active;
  active = null;
  window.clearInterval(rec.timerHandle);
  window.clearTimeout(rec.capHandle);
  window.clearInterval(rec.heartbeatHandle);
  detachOrientationListener();
  setRecordingTab(false);
  document.body.classList.remove('is-recording');
  document.body.classList.add('is-finishing');
  updateStudioMode();
  setScreenPreviewControls(!!screenPreviewStream);
  setPauseResumeControl(false);
  control.setState('recording', COPY.finishing);
  control.setDisabled(true);
  try {
    // Must run before session.stop() -- see captureThumbnailDataUri()'s doc
    // comment for why this exact point (paintTimer still running, canvas
    // holding near-final content) was chosen over frame 0.
    const thumbnailDataUri = captureThumbnailDataUri(rec.session.canvas);
    const durationS = await rec.session.stop(); // flushes final chunk first
    // Give the last ondataavailable microtask a beat to land in the uploader.
    await new Promise((r) => setTimeout(r, 150));
    const result = await rec.uploader.finish(durationS, thumbnailDataUri ?? undefined);
    console.log(`[clip] finished slug=${rec.slug} status=${result.status}`);
    window.location.href = rec.watchUrl;
  } catch (err) {
    console.error('[clip] stop/finish failed', err);
    setDeviceStatus(
      `Finishing upload failed: ${(err as Error).message}. Your chunks are journaled locally; reload to resume.`,
      'error',
    );
    // SC-31: showPreview() appended the composited recording canvas to
    // #preview-box back when recording started, and updateStudioMode()'s
    // hasPreview check treats that canvas as a live preview on its own --
    // screenPreviewStream/livePreviewStream are already null by this point
    // (consumed at beginFlow()'s start), so removing is-recording/is-finishing
    // below isn't enough to clear is-studio. resetPreview() is the same
    // teardown already used on the sibling failure paths (start-recording
    // catch, countdown-cancel) for this exact canvas.
    resetPreview();
    document.body.classList.remove('is-recording', 'is-finishing');
    updateStudioMode();
    detachOrientationListener();
    setRecordingTab(false);
    control.setState('error', {
      ...COPY.error,
      title: 'Upload failed.',
      detail: 'Your recording is saved locally — reload this page to resume.',
    });
    control.setDisabled(false);
    void refreshLibrary();
  }
}

/** Crash recovery: unfinished journal sessions → offer to finish uploading. */
async function checkRecovery(): Promise<void> {
  const sessions = await journal.listUnfinishedSessions();
  if (sessions.length === 0) return;
  const banner = $('recovery-banner');
  banner.classList.remove('hidden');
  $('recovery-text').textContent = `${sessions.length} unfinished recording${sessions.length > 1 ? 's' : ''} found from a previous session.`;
  $('recovery-btn').onclick = async () => {
    banner.classList.add('hidden');
    for (const s of sessions) {
      try {
        await recoverSession(s.slug, s.partSize);
      } catch (err) {
        console.error(`[clip] recovery failed for ${s.slug}`, err);
      }
    }
    void refreshLibrary();
  };
}

async function recoverSession(slug: string, sessionPartSize: number): Promise<void> {
  const status = (await fetch(`/api/recordings/${slug}`).then((r) => (r.ok ? r.json() : null))) as {
    status: string;
  } | null;
  if (!status || status.status !== 'recording') {
    await journal.finishSession(slug);
    return;
  }
  const chunks = await journal.chunksFor(slug);
  if (chunks.length === 0) {
    await journal.finishSession(slug);
    return;
  }
  console.log(`[clip] recovering ${slug}: ${chunks.length} journaled chunks`);
  const uploader = new PartUploader(slug, journal, sessionPartSize);
  // Journal rows are the un-acked byte suffix, in seq order = byte order.
  // NOTE (MVP): assumes acked parts were pruned; resume continues numbering
  // from part 1 only when nothing was acked. Full offset-aware resume is a
  // follow-up — see README "Known gaps".
  for (const chunk of chunks) {
    await uploader.addChunk(new Blob([chunk.bytes]));
  }
  await uploader.finish(0);
}

// ---------------------------------------------------------------------------
// Auth gate: recording requires a clip account (see client/auth.ts) — the
// rest of the app only initializes once one exists.

// #auth-loading is new (SC-150); unlike #auth-gate/#app-content it isn't
// guaranteed to exist -- the service worker can pair a stale cached /record
// document from before this element existed with an already-refreshed
// app.js (e.g. a prior visit to /g/:slug, which shares this same bundle,
// refreshed app.js's cache without refreshing /record's own cached HTML).
// $()'s unconditional cast would throw on that combination; look this one
// up defensively everywhere it's touched.
function hideAuthLoading(): void {
  document.getElementById('auth-loading')?.classList.add('hidden');
}

function showApp(user: CurrentUser): void {
  hideAuthLoading();
  $('auth-gate').classList.add('hidden');
  $('app-content').classList.remove('hidden');
  const chip = $('account-chip');
  chip.classList.remove('hidden');
  $('account-name').textContent = user.displayName;
  $('logout-btn').addEventListener(
    'click',
    async () => {
      await logout();
      location.reload();
    },
    { once: true },
  );
}

async function loadGroupContext(): Promise<boolean> {
  if (!groupSlug) return true;
  const res = await fetch(`/api/groups/${encodeURIComponent(groupSlug)}`);
  if (res.ok) {
    currentGroup = (await res.json()) as { name: string; slug: string; role: 'owner' | 'member' };
    document.title = `${currentGroup.name} — clip`;
    $('library-heading').textContent = `${currentGroup.name} library`;
    await loadWorkspaceMembers(currentGroup.slug);
    return true;
  }
  setDeviceStatus('This workspace is unavailable. Returning to your personal library.', 'error');
  window.setTimeout(() => { location.href = '/record'; }, 1800);
  return false;
}

function initialsFor(displayName: string): string {
  return displayName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] || '')
    .join('')
    .toUpperCase();
}

function avatarColorFor(displayName: string): string {
  const colors = ['var(--yellow)', 'var(--blue)', 'var(--green)', 'var(--pink)', 'var(--orange)', 'var(--violet)'];
  let hash = 0;
  for (const character of displayName) hash = (hash * 31 + character.charCodeAt(0)) | 0;
  return colors[Math.abs(hash) % colors.length];
}

async function loadWorkspaceMembers(slug: string): Promise<void> {
  const roster = $('workspace-roster');
  const chips = $('workspace-member-chips');
  chips.replaceChildren();
  roster.classList.add('hidden');
  const res = await fetch(`/api/groups/${encodeURIComponent(slug)}/members`);
  if (!res.ok) return;
  const { members } = (await res.json()) as { members: { displayName: string; avatarUrl: string | null }[] };
  if (!members.length) return;
  for (const member of members) {
    const chip = document.createElement('span');
    chip.className = 'workspace-member-chip';
    chip.title = member.displayName;
    if (member.avatarUrl) {
      const avatar = document.createElement('img');
      avatar.src = member.avatarUrl;
      avatar.alt = member.displayName;
      // A Fawnroad avatar URL is an external link Clip doesn't control --
      // it can 404 or time out after this chip is already built. Falling
      // back to the same initials treatment used for a genuinely-absent
      // avatarUrl keeps a failed load from showing a permanently broken
      // image icon instead.
      avatar.addEventListener(
        'error',
        () => {
          avatar.remove();
          chip.style.backgroundColor = avatarColorFor(member.displayName);
          chip.textContent = initialsFor(member.displayName);
        },
        { once: true },
      );
      chip.appendChild(avatar);
    } else {
      chip.style.backgroundColor = avatarColorFor(member.displayName);
      chip.textContent = initialsFor(member.displayName);
    }
    chips.appendChild(chip);
  }
  $('workspace-member-count').textContent = `${members.length} ${members.length === 1 ? 'member' : 'members'}`;
  roster.classList.remove('hidden');
}

async function populateWorkspaceSwitcher(): Promise<void> {
  const switcher = $<HTMLSelectElement>('workspace-switcher');
  const res = await fetch('/api/groups');
  if (!res.ok) return;
  const { groups } = (await res.json()) as { groups: { name: string; slug: string }[] };
  switcher.innerHTML = '';
  const personal = new Option('Personal', '');
  personal.title = 'Personal';
  personal.selected = !groupSlug;
  switcher.add(personal);
  for (const group of groups) {
    const option = new Option(group.name, group.slug);
    option.title = group.name;
    option.selected = group.slug === groupSlug;
    switcher.add(option);
  }
  const updateAccessibleName = () => {
    const workspaceName = switcher.selectedOptions[0]?.text || 'Personal';
    switcher.title = workspaceName;
    switcher.setAttribute('aria-label', `Workspace: ${workspaceName}`);
  };
  updateAccessibleName();
  $('workspace-control').classList.remove('hidden');
  switcher.addEventListener('change', () => {
    updateAccessibleName();
    location.href = switcher.value ? `/g/${encodeURIComponent(switcher.value)}` : '/record';
  });
}

// Sign-in is a plain navigation to /auth/fawnroad/start (no JS needed to
// trigger it) — the only client-side work is surfacing a failed round trip
// when the callback redirects back here with ?auth_error=.
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  not_configured: "Sign-in with Fawnroad isn't set up for this environment yet.",
  expired: 'That sign-in attempt expired or was already used — please try again.',
  failed: 'Sign-in with Fawnroad failed — please try again.',
};

function showAuthErrorIfAny(): void {
  const code = params.get('auth_error');
  if (!code) return;
  const errorEl = $('auth-error');
  errorEl.textContent = AUTH_ERROR_MESSAGES[code] || 'Sign-in failed — please try again.';
  errorEl.classList.remove('hidden');
  const url = new URL(location.href);
  url.searchParams.delete('auth_error');
  history.replaceState(null, '', url.toString());
}

// ---------------------------------------------------------------------------

async function initApp(): Promise<void> {
  await populateWorkspaceSwitcher();
  await journal.init();
  initToggleRow();
  bindBubbleInteractions($<HTMLVideoElement>('camera-preview-video'));
  bindBubbleInteractions($<HTMLVideoElement>('camera-preview-video-2'));
  window.addEventListener('resize', applyPreviewBubble);
  window.addEventListener('beforeunload', (event) => {
    // Same class that drives the on-screen recording indicator, so pause
    // (which never clears is-recording) still counts as "in the middle of
    // recording" per the user's framing — only a real stop clears it.
    if (!document.body.classList.contains('is-recording')) return;
    event.preventDefault();
    event.returnValue = ''; // legacy browsers require a non-empty string to trigger the prompt
  });
  await initDeviceDialog();

  control.setState('idle', COPY.idle);
  if (debugState && ['idle', 'armed', 'recording', 'paused', 'error'].includes(debugState)) {
    const mode = getCaptureMode();
    const armedCopy = mode === 'camera' ? COPY.armedCamera : COPY.armedScreen;
    control.setState(debugState as any, debugState === 'armed' ? armedCopy : ((COPY as any)[debugState] ?? COPY.idle));
  }

  control.onClick(() => {
    if (document.body.classList.contains('is-recording')) {
      void stopRecording();
    } else if (!document.body.classList.contains('is-finishing')) {
      void beginFlow();
    }
  });

  $<HTMLButtonElement>('pause-resume-btn').addEventListener('click', () => {
    if (!active) return;
    if (active.session.isPaused()) {
      active.session.resume();
      control.setState('recording', COPY.recording);
      setPauseResumeControl(true);
      setRecordingTab(true, fmtClock(active.session.elapsedS()));
    } else {
      active.session.pause();
      control.setState('paused', COPY.paused);
      setPauseResumeControl(true, true);
      setRecordingTab(true, fmtClock(active.session.elapsedS()), true);
    }
  });

  $('hud-copy-btn').addEventListener('click', async () => {
    const link = $<HTMLAnchorElement>('hud-watch-link');
    try {
      await navigator.clipboard.writeText(link.href);
      announce.textContent = 'Watch link copied.';
      showToast('Copied!');
    } catch {
      /* clipboard permission denied — link is still selectable text */
    }
  });

  $('library-retry-btn').addEventListener('click', () => void refreshLibrary());
  await refreshLibrary();
  await checkRecovery();
  setInterval(() => {
    if (!active) void refreshLibrary();
  }, 5000);
}

async function main(): Promise<void> {
  const footerYear = document.getElementById('footer-year');
  if (footerYear) footerYear.textContent = String(new Date().getFullYear());

  // .catch(): a rejected check (offline, DNS failure, malformed response)
  // must fall through to the signed-out branch below, same as a resolved
  // `null` -- otherwise, with #auth-gate now hidden by default (SC-150),
  // main() would throw here and leave the user stuck on the loading
  // placeholder forever instead of the still-usable sign-in gate.
  const user = await fetchCurrentUser().catch(() => null);
  if (user) {
    // initApp() (via initToggleRow()) is what hides the Screen toggle on
    // devices without getDisplayMedia support -- it must finish before
    // #app-content is revealed, or a slow /api/groups* fetch lets the user
    // see the toggle in its wrong (unsupported) state for a moment (SC-23).
    // showApp() is deliberately called last on every branch below so the
    // reveal always happens after that settles, on both the plain "/" path
    // and the "/g/:slug" group path.
    try {
      if (await loadGroupContext()) await initApp();
      showApp(user);
    } catch (err) {
      // Never leave a genuinely authenticated user stuck on the loading
      // placeholder over an unexpected init failure (a rejected group
      // fetch, IndexedDB failing to open, etc.) until record.html's generic
      // 30s backstop eventually catches it. Falling back to the sign-in
      // gate immediately is not semantically "correct" (they are signed
      // in), but it's a real, actionable recovery path -- reload, or sign
      // in again -- instead of a long, needless wait.
      console.error('[clip] initialization failed after sign-in; falling back to the sign-in gate:', err);
      hideAuthLoading();
      $('auth-gate').classList.remove('hidden');
    }
  } else {
    showAuthErrorIfAny();
    hideAuthLoading();
    $('auth-gate').classList.remove('hidden');
  }

  // E2E readiness signal: when logged out, means "the auth gate (and any
  // ?auth_error= message) is rendered"; when logged in, means the full app
  // (device enumeration, journal open, click handlers) is ready. Tests must
  // not interact before this.
  (window as unknown as { __clipReady: boolean }).__clipReady = true;
}

void main();
